/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

import { BackArrow } from '@ahamove/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Center,
  // FocusTrap,
  Group,
  PinInput,
  rem,
  Space,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as z from 'zod';
import { useAuth } from '../auth-provider';
import { AuthStep } from '../lib/constants';
import { hidePhoneNumber } from '../lib/utils';
import { authLocales, createTranslations, type Locales } from '../locales';
import { useAuthModal } from '../modals/auth-modal-provider';
import { useAuthStore } from '../store/auth-store-provider';

const passcodeSchema = z.object({
  passcode: z.string().length(6, 'Passcode must be 6 digits'),
});

type PasscodeFormValues = z.infer<typeof passcodeSchema>;
type Props = {
  lang?: keyof Locales;
};
export default function PasscodeForm({ lang = 'vi' }: Props) {
  const tAuth = createTranslations({ locales: authLocales, lang });

  const {
    apiError,
    activeAuthStep,
    setActiveAuthStep,
    phoneNumber,
    setApiError,
  } = useAuthStore((state) => state);

  const {
    loading,
    handleActivateUser,
    loginData,
    handleForceLoginOTP,
    setShowModalSelectOtpChannel,
  } = useAuth();

  const { openAuthModal } = useAuthModal();

  const {
    control,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors },
  } = useForm<PasscodeFormValues>({
    resolver: zodResolver(passcodeSchema),
    defaultValues: {
      passcode: '',
    },
  });

  useEffect(() => {
    if (activeAuthStep === AuthStep.PassCode || apiError || !loading) {
      setFocus('passcode');
      if (apiError) {
        reset();
      }
    }
  }, [activeAuthStep, apiError, reset, setFocus, loading]);

  const onSubmit = (data: PasscodeFormValues) => {
    handleActivateUser(data.passcode);
  };

  const handleBack = () => {
    reset();
    setApiError(null);
    setActiveAuthStep(AuthStep.PhoneNumber);
  };

  const handlePasscodeComplete = (value: string) => {
    if (value.length === 6) {
      handleActivateUser(value);
    }
  };

  const handleSwitchToLoginWithOTP = () => {
    if (!phoneNumber) return;

    reset();
    // setActiveAuthStep(AuthStep.OTP);

    handleForceLoginOTP(phoneNumber);
    // setShowModalSelectOtpChannel(true);
  };

  return (
    <Box
      display="flex"
      w="100%"
      flex={1}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      onReset={() => reset()}
      pb="xs"
      style={{ flexDirection: 'column' }}
    >
      <Stack align="stretch" flex={1} gap="sm">
        <Group wrap="nowrap" hiddenFrom="sm">
          <ActionIcon
            size={rem(44)}
            radius="xl"
            variant="subtle"
            color="neutral"
            aria-label="Back"
            my={rem(-10)}
            ml={rem(-10)}
            onClick={handleBack}
          >
            <BackArrow width={24} height={24} />
          </ActionIcon>
        </Group>
        <Title order={2}>{tAuth('enterPassword')}</Title>
        <Text size="md" c="neutral.6" fw={500}>
          {tAuth('loggingInWithPhoneNumber')}
          <Text
            component="span"
            size="md"
            fw={700}
            c="neutral.7"
          >{`+${hidePhoneNumber(loginData.id)}`}</Text>
        </Text>
        <Stack gap={0} w="100%" mt="sm">
          <Controller
            name="passcode"
            control={control}
            render={({ field: { onChange, value, ...props } }) => (
              <PinInput
                {...props}
                // disabled={loading}
                length={6}
                gap={rem(8)}
                inputMode="numeric"
                size="lg"
                placeholder=""
                mask
                value={value}
                onChange={onChange}
                onComplete={handlePasscodeComplete}
                error={!!errors.passcode || !!apiError}
                styles={{
                  root: {
                    '--mantine-color-gray-4': 'var(--mantine-color-neutral-0)',
                  },
                  input: {
                    backgroundColor: 'var(--mantine-color-neutral-0)',
                  },
                }}
              />
            )}
          />
          <Text fw={500} size="sm" c="red.5" my={rem(8)}>
            {errors.passcode?.message ??
              apiError?.description ??
              apiError?.title}
          </Text>
          <Space h={rem(16)} w="100%" />

          <Group gap={rem(8)}>
            <Button
              variant="transparent"
              styles={{
                root: {
                  fontSize: rem(16),
                  lineHeight: rem(24),
                  fontWeight: 700,

                  padding: 0,
                },
              }}
              onClick={handleSwitchToLoginWithOTP}
            >
              {tAuth('loginWithOTP')}
            </Button>

            <Text size="sm" c="neutral.5" fw={500}>
              hoặc
            </Text>

            <Button
              variant="transparent"
              styles={{
                root: {
                  fontSize: rem(16),
                  lineHeight: rem(24),
                  fontWeight: 700,

                  padding: 0,
                },
              }}
              onClick={() =>
                openAuthModal('confirmResetPasscode', { mobile: loginData.id })
              }
            >
              {tAuth('forgotPassword')}
            </Button>
          </Group>
        </Stack>

        <Space h={rem(32)} w="100%" />

        <Center mt="auto">
          <Anchor size="sm" fw={600} onClick={handleBack} c="red.6">
            {tAuth('changePhoneNumber')}
          </Anchor>
        </Center>
      </Stack>
    </Box>
  );
}
